/**
 * The columns of the two nomination tables — the event detail page's Attendance List and the
 * Nomination List page — in one list, so the two show the same columns, by the same names, in the
 * same order.
 *
 * Shown by default: the set Event Hub Testing doc #34 lists — Contact Name, Company Name, Job
 * Title, Phone, Email, BD Name, Nomination Status, Registration Status, Attendance Confirmed,
 * Attendance Status. Everything else a nomination carries is offered in the table's preferences.
 *
 * Two kinds of column belong to one table only:
 *   - Registration / Attendance Status are campaign-member facts, which only ListEventAttendees
 *     returns; a nomination (ListNominations) has neither, so the Nomination List leaves them out.
 *   - Event Name, Event Owner and Actions belong to the Nomination List, which spans events and
 *     is where decisions are made. So does BD BU: the Attendance List is read by the guest's BU
 *     (Customer BU, which its BU filter is on), and the BD's BU there only invited confusion.
 *   - Campaigns and SFDC Campaign ID — the name and id of every SFDC campaign the person is a
 *     member of — are the Attendance List's, and only while SUB_CAMPAIGN_ATTENDEES_ENABLED
 *     (config.ts) is on. Both hidden until switched on in the preferences.
 *
 * Each table renders its own cells; {@link nominationCellText} gives both the text of the columns
 * read straight off the nomination, so they cannot read differently either.
 *
 * Not columns: the per-event nomination-form answers (each event asks its own questions, and the
 * Nomination List spans events — they are in the detail modal), and Company Revenue Size (the
 * service does not store it).
 */
import type { Nomination } from '../client/EventCalendarClient';
import { SUB_CAMPAIGN_ATTENDEES_ENABLED } from '../../config';
import { formatDateTime } from './eventDates';

export type NominationTable = 'attendance' | 'nominations';

export interface NominationColumn {
  id: string;
  label: string;
  /** Shown until the reader changes it in the preferences. */
  defaultVisible: boolean;
  /** Set for a column only one of the two tables has. */
  only?: NominationTable;
}

export const NOMINATION_COLUMNS: readonly NominationColumn[] = [
  { id: 'guest', label: 'Contact Name', defaultVisible: true },
  { id: 'eventName', label: 'Event Name', defaultVisible: true, only: 'nominations' },
  { id: 'company', label: 'Company Name', defaultVisible: true },
  { id: 'jobTitle', label: 'Job Title', defaultVisible: true },
  { id: 'phone', label: 'Phone', defaultVisible: true },
  { id: 'email', label: 'Email', defaultVisible: true },
  { id: 'bdName', label: 'BD Name', defaultVisible: true },
  { id: 'status', label: 'Nomination Status', defaultVisible: true },
  { id: 'registration', label: 'Registration Status', defaultVisible: true, only: 'attendance' },
  { id: 'confirmed', label: 'Attendance Confirmed', defaultVisible: true },
  { id: 'attendance', label: 'Attendance Status', defaultVisible: true, only: 'attendance' },
  { id: 'campaigns', label: 'Campaigns', defaultVisible: false, only: 'attendance' },
  { id: 'sfdcCampaignId', label: 'SFDC Campaign ID', defaultVisible: false, only: 'attendance' },
  { id: 'sfdcAccountId', label: 'SFDC Account ID', defaultVisible: false },
  { id: 'accountTags', label: 'Account Tags', defaultVisible: false },
  { id: 'customerBu', label: 'Customer BU', defaultVisible: false },
  { id: 'jobRole', label: 'Job Role', defaultVisible: false },
  { id: 'bdPhone', label: 'BD Phone', defaultVisible: false },
  { id: 'bdBu', label: 'BD BU', defaultVisible: false, only: 'nominations' },
  { id: 'submittedOn', label: 'Submitted On', defaultVisible: false },
  { id: 'reviewedBy', label: 'Reviewed By', defaultVisible: false },
  { id: 'comments', label: 'Review Comments', defaultVisible: false },
  { id: 'eventOwner', label: 'Event Owner', defaultVisible: false, only: 'nominations' },
  { id: 'actions', label: 'Actions', defaultVisible: true, only: 'nominations' },
];

const SUB_CAMPAIGN_COLUMNS = new Set(['campaigns', 'sfdcCampaignId']);

/** One table's columns, in order. */
export function columnsFor(
  table: NominationTable,
  subCampaigns: boolean = SUB_CAMPAIGN_ATTENDEES_ENABLED,
): NominationColumn[] {
  return NOMINATION_COLUMNS.filter(
    (c) => (!c.only || c.only === table) && (!SUB_CAMPAIGN_COLUMNS.has(c.id) || subCampaigns),
  );
}

/** One table's default `contentDisplay` preference. */
export function defaultContentDisplay(
  table: NominationTable,
  subCampaigns: boolean = SUB_CAMPAIGN_ATTENDEES_ENABLED,
): { id: string; visible: boolean }[] {
  return columnsFor(table, subCampaigns).map((c) => ({ id: c.id, visible: c.defaultVisible }));
}

/** The label of a column, for its header. */
export function columnLabel(id: string): string {
  return NOMINATION_COLUMNS.find((c) => c.id === id)?.label ?? id;
}

/**
 * The text of a column read straight off the nomination, or undefined when it has none. Only the
 * plain-text columns; the rest (names, status, the toggle, actions) are rendered by each table.
 */
export function nominationCellText(id: string, n: Nomination | undefined): string | undefined {
  if (!n) return undefined;
  const text = (v: string | undefined) => v?.trim() || undefined;
  switch (id) {
    case 'sfdcAccountId':
      return text(n.accountInformation?.sfdcAccountId);
    case 'accountTags':
      return n.accountInformation?.accountTags?.length
        ? n.accountInformation.accountTags.join(', ')
        : undefined;
    case 'customerBu':
      return text(n.guestInformation?.businessUnit);
    case 'bdPhone':
      return text(n.bdCellPhone);
    case 'bdBu':
      return text(n.bdBusinessUnit);
    case 'submittedOn':
      return n.createdAt ? formatDateTime(n.createdAt) : undefined;
    case 'reviewedBy':
      return text(n.decidedBy);
    case 'comments':
      return text(n.rejectReason);
    default:
      return undefined;
  }
}
